/**
 * Password hashing with PBKDF2-SHA256 (Web Crypto: Cloudflare Workers and Node ≥ 20).
 * Cloudflare Workers allow at most 100 000 PBKDF2 iterations, so that is the default.
 * Stored format: pbkdf2-sha256$<iterations>$<salt b64url>$<hash b64url>. The iteration count lives in the
 * string, so it can be raised later without invalidating existing passwords.
 */
export const PASSWORD_ITERATIONS = 100_000;
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

const enc = new TextEncoder();

function toB64u(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromB64u(text: string): Uint8Array {
  const b64 = text.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (text.length % 4)) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function derive(password: string, salt: Uint8Array, iterations: number): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey("raw", enc.encode(password), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits({ name: "PBKDF2", hash: "SHA-256", salt: salt as BufferSource, iterations }, key, 256);
  return new Uint8Array(bits);
}

function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= (a[i] ?? 0) ^ (b[i] ?? 0);
  return diff === 0;
}

export async function hashPassword(password: string, iterations: number = PASSWORD_ITERATIONS): Promise<string> {
  const salt = new Uint8Array(16);
  crypto.getRandomValues(salt);
  const hash = await derive(password, salt, iterations);
  return `pbkdf2-sha256$${iterations}$${toB64u(salt)}$${toB64u(hash)}`;
}

const DUMMY_SALT = new Uint8Array(16);

/**
 * Constant-effort check. When there is no stored hash (unknown email, or no password set yet) it still
 * performs one derivation, so response time does not reveal whether an account has a password.
 */
export async function verifyPassword(password: string, stored: string | null): Promise<boolean> {
  const parts = stored ? stored.split("$") : [];
  const [scheme, iterText, saltText, hashText] = parts;
  const iterations = Number(iterText);
  if (scheme !== "pbkdf2-sha256" || !saltText || !hashText || !Number.isInteger(iterations) || iterations < 1 || iterations > PASSWORD_ITERATIONS) {
    await derive(password, DUMMY_SALT, PASSWORD_ITERATIONS);
    return false;
  }
  const actual = await derive(password, fromB64u(saltText), iterations);
  return equalBytes(actual, fromB64u(hashText));
}
